/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { isValidGuid, isObject } from "utils/empty-exists";
import { DEFAULT_IMAGE_SETTINGS } from "cms/images/image-inpage-editor-v2/image-helpers";
import { Logger } from "cms/utils/log/logger";

const useSimplicityImage = function () {

    const { token } = useAuth();

    const getImage = async (guid) => {
        const image = await SimpliCityAdminSDK.images.findOne(token, guid);
        return image;
    };

    const isValidImage = (image) => {
        if (!isObject(image)) {
            Logger.warn("Image was not a valid image");
            return false;
        }
        const guid = image?.guid;
        if (!isValidGuid(guid)) {
            Logger.warn("Image did not have a valid guid");
            return false;
        }
        return true;
    };

    const isValidSettings = (settings) => {
        if (!isObject(settings)) {
            Logger.warn("Settings were not valid.");
            return false;
        }
        const options = settings?.options;
        if (!isObject(options)) {
            Logger.warn("Settings were not valid.");
            return false;
        }
        return true;
    };

    const saveBannerImageToModel = async (model, image, settings) => {
        const image_guid = image?.guid;
        const model_guid = model?.guid;
        if (!isObject(image)) {
            throw Error("Image was not provided");
        }
        if (!isObject(model)) {
            throw Error("Model was not provided");
        }
        if (!isValidGuid(image_guid)) {
            throw Error("Image did not have a guid");
        }
        if (!isValidGuid(model_guid)) {
            throw Error("Model did not have a guid");
        }
        if (!isObject(settings)) {
            Logger.warn("Image settings were not provided, so using defaults.");
            settings = DEFAULT_IMAGE_SETTINGS;
        }
        const _model = structuredClone(model);
        const imageData = {
            image: image_guid,
            settings: settings
        };
        _model.banner_image = imageData;
        delete _model.created_at;
        delete _model.updated_at;
        const model_response = await SimpliCityAdminSDK.model.update(token, model_guid, _model);
        Logger.warn("Banner Image (model) saved at API.");
        Logger.debug(model_response);
        return model_response;
    };

    const prepareImageData = async (image, settings) => {
        const { guid: image_guid } = image || {};
        if (!isObject(image)) {
            throw Error("Image was not provided");
        }
        if (!isValidGuid(image_guid)) {
            throw Error("Image did not have a guid");
        }
        if (!isObject(settings)) {
            Logger.warn("Image settings were not provided, so using defaults.");
            settings = DEFAULT_IMAGE_SETTINGS;
        }
        // const _model = structuredClone(model)
        const imageData = {
            image: image,
            settings: settings
        };
        return imageData;
    };

    const removeBannerImageFromModel = async (model) => {
        if (!isObject(model)) {
            throw Error("Model was not provided");
        }
        const { guid } = model || {};
        if (!isValidGuid(guid)) {
            throw Error("Model did not have a guid");
        }
        const _model = structuredClone(model);
        _model.banner_image = null;
        delete _model.created_at;
        delete _model.updated_at;
        const model_response = await SimpliCityAdminSDK.model.update(token, guid, _model);
        Logger.debug(model_response);
        return model_response;
    };

    const saveImage = async (image) => {
        const { guid } = image || {};
        if (!isObject(image)) {
            throw Error("Image was not provided to save");
        }
        if (!isValidGuid(guid)) {
            throw Error("Image did not hav a guid");
        }
        const image_response = await SimpliCityAdminSDK.images.update(token, guid, image);
        Logger.debug(image_response);
        return image_response;
    };

    const deleteImage = async (image) => {
        const { guid } = image || {};
        if (!isObject(image)) {
            throw Error("Image was not provided to delete");
        }
        if (!isValidGuid(guid)) {
            throw Error("Image did not have a guid");
        }
        const image_response = await SimpliCityAdminSDK.images.delete(token, guid);
        Logger.debug(image_response);
        return image_response;
    };

    const archiveImage = async (guid) => {
        const result = await SimpliCityAdminSDK.images.archive(token, guid);
        Logger.debug("archive image...");
        const { status } = result || {};
        Logger.debug(status);
        return status;
    };

    const restoreImage = async (guid) => {
        const result = await SimpliCityAdminSDK.images.restoreArchive(token, guid);
        Logger.debug("restore image...");
        // const status = result?.status;
        const image = result?.image;
        Logger.debug(image);
        return image;
    };

    const renameImage = async (guid, renamed_file_name) => {
        const result = await SimpliCityAdminSDK.images.rename(token, guid, renamed_file_name);
        Logger.debug("rename image...");
        // const status = result?.status;
        const image = result?.image;
        Logger.debug(image);
        return image;
    };

    const getPlacements = async (guid) => {
        if (!guid) {
            return [];
        }
        const placements = await SimpliCityAdminSDK.images.placements(token, guid);
        Logger.debug("image placements", { placements });
        return placements;
    };

    return {
        getImage,
        isValidImage,
        isValidSettings,
        // createImage,
        saveImage,
        deleteImage,
        removeBannerImageFromModel,
        saveBannerImageToModel,
        archiveImage,
        restoreImage,
        renameImage,
        prepareImageData,
        getPlacements,
    };
};

export { useSimplicityImage };
